import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, Clock, Copy, FileText, Gift, Lock, Upload, XCircle,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { formatPrice } from '../store';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/ToastProvider';
import {
  subscriptionApi, SubscriptionPaymentItem, SubscriptionPlan, SubscriptionState,
} from '../features/subscription/api';

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' });

const STATUS: Record<SubscriptionPaymentItem['status'], { label: string; cls: string; Icon: React.FC<any> }> = {
  pending: { label: "Ko'rib chiqilmoqda", cls: 'bg-warning-50 text-warning-700', Icon: Clock },
  approved: { label: 'Tasdiqlangan', cls: 'bg-success-50 text-success-600', Icon: CheckCircle2 },
  rejected: { label: 'Rad etilgan', cls: 'bg-danger-50 text-danger-600', Icon: XCircle },
};

export const Subscription: React.FC = () => {
  const toast = useToast();
  const refreshSeller = useAuthStore((s) => s.refreshSeller);
  const [state, setState] = useState<SubscriptionState | null>(null);
  const [error, setError] = useState('');
  const [payPlan, setPayPlan] = useState<SubscriptionPlan | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await subscriptionApi.get();
      setState(res.data);
      setError('');
    } catch (e: any) {
      setError(e.response?.data?.error || "Obuna ma'lumotini yuklab bo'lmadi");
    }
  }, []);

  useEffect(() => {
    load();
    // Admin tasdiqlagan bo'lishi mumkin — auth holatini ham yangilaymiz,
    // shunda blok darhol yechiladi.
    refreshSeller();
  }, [load, refreshSeller]);

  const openReceipt = async (id: number) => {
    try {
      const res = await subscriptionApi.receipt(id);
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch {
      toast.error("Chekni ochib bo'lmadi");
    }
  };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="page-bar flex items-center gap-2">
        <Link to="/profile" className="btn-ghost p-2 -ml-2 rounded-lg" aria-label="Orqaga">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Obuna</h1>
      </div>

      {error && <div className="card p-4 text-danger-600 text-sm">{error}</div>}
      {!state && !error && <div className="card p-6 text-center text-gray-400 text-sm">Yuklanmoqda...</div>}

      {state && (
        <>
          <StatusCard state={state} />

          {state.has_pending && (
            <div className="card p-4 flex gap-3 bg-warning-50 border-warning-100">
              <Clock size={20} className="text-warning-700 shrink-0 mt-0.5" />
              <div className="text-sm text-warning-700">
                <div className="font-semibold">Chekingiz tekshirilmoqda</div>
                Admin to'lovni tasdiqlagach obuna avtomatik uzaytiriladi.
              </div>
            </div>
          )}

          {!state.can_pay ? (
            <div className="card p-4 text-sm text-gray-500">
              Obunani do'kon egasi to'laydi. Savol bo'lsa, unga murojaat qiling.
            </div>
          ) : state.plans.length === 0 ? (
            <div className="card p-4 text-sm text-gray-500">Hozircha tarif yo'q. Admin bilan bog'laning.</div>
          ) : (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-500 px-1">Tariflar</h2>
              {state.plans.map((plan) => (
                <div key={plan.id} className="card p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900">{plan.name}</div>
                    <div className="text-sm text-gray-500">{plan.duration_label}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">{formatPrice(plan.price)} so'm</div>
                    {plan.price !== plan.base_price && (
                      <div className="text-xs text-gray-400 line-through">{formatPrice(plan.base_price)}</div>
                    )}
                  </div>
                  <button
                    className="btn btn-primary shrink-0"
                    disabled={state.has_pending}
                    onClick={() => setPayPlan(plan)}
                  >
                    To'lash
                  </button>
                </div>
              ))}
            </div>
          )}

          {state.payments.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-500 px-1">Tarix</h2>
              {state.payments.map((p) => (
                <PaymentRow key={p.id} payment={p} onReceipt={() => openReceipt(p.id)} />
              ))}
            </div>
          )}
        </>
      )}

      {state && payPlan && (
        <PayModal
          plan={payPlan}
          requisites={state.requisites}
          onClose={() => setPayPlan(null)}
          onDone={(next) => {
            setState(next);
            setPayPlan(null);
            toast.success("Chek yuborildi. Admin tekshirib tasdiqlaydi.");
          }}
        />
      )}
    </div>
  );
};

const StatusCard: React.FC<{ state: SubscriptionState }> = ({ state }) =>
  state.active ? (
    <div className="card p-5 bg-primary-50 border-primary-100">
      <div className="text-sm text-primary-800 font-medium">Obuna faol</div>
      <div className="text-3xl font-bold text-gray-900 mt-1">{state.days_left} kun</div>
      <div className="text-sm text-gray-500 mt-1">{fmtDay(state.until)} gacha</div>
    </div>
  ) : (
    <div className="card p-5 bg-danger-50 border-danger-100">
      <div className="flex items-center gap-2 text-danger-600 font-semibold">
        <Lock size={18} /> Obuna muddati tugagan
      </div>
      <p className="text-sm text-gray-600 mt-2">
        {fmtDay(state.until)} da tugadi. Do'kon bo'limlari to'lov tasdiqlangunicha yopiq —
        ma'lumotlaringiz saqlanib turibdi.
      </p>
    </div>
  );

const PaymentRow: React.FC<{ payment: SubscriptionPaymentItem; onReceipt: () => void }> = ({ payment, onReceipt }) => {
  const meta = STATUS[payment.status];
  const free = payment.kind === 'free';
  return (
    <div className="card p-4 space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 flex items-center gap-1.5">
            {free && <Gift size={16} className="text-primary-800" />}
            {payment.plan_name} · {payment.duration_label}
          </div>
          <div className="text-xs text-gray-400">{fmtDay(payment.created_at)}</div>
        </div>
        {!free && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.cls}`}>
            <meta.Icon size={12} /> {meta.label}
          </span>
        )}
      </div>
      {!free && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">{formatPrice(payment.amount)} so'm</span>
          {payment.has_receipt && (
            <button onClick={onReceipt} className="text-primary-800 font-medium flex items-center gap-1">
              <FileText size={14} /> Chek
            </button>
          )}
        </div>
      )}
      {payment.status === 'approved' && payment.period_end && (
        <div className="text-xs text-gray-500">{fmtDay(payment.period_end)} gacha uzaytirildi</div>
      )}
      {payment.note && free && <div className="text-xs text-gray-500">{payment.note}</div>}
      {payment.status === 'rejected' && payment.rejection_reason && (
        <div className="text-sm bg-danger-50 text-danger-600 rounded-lg px-3 py-2">
          <span className="font-medium">Sabab:</span> {payment.rejection_reason}
        </div>
      )}
    </div>
  );
};

const PayModal: React.FC<{
  plan: SubscriptionPlan;
  requisites: SubscriptionState['requisites'];
  onClose: () => void;
  onDone: (state: SubscriptionState) => void;
}> = ({ plan, requisites, onClose, onDone }) => {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [amount, setAmount] = useState(String(Math.round(parseFloat(plan.price))));
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text.replace(/\s/g, ''));
      toast.success('Nusxalandi');
    } catch {
      /* clipboard ruxsati yo'q — matnni qo'lda ko'chiradi */
    }
  };

  const submit = async () => {
    setError('');
    if (!file) return setError('Chek rasmini yuklang');
    if (!(parseFloat(amount) > 0)) return setError("To'langan summani kiriting");
    setBusy(true);
    try {
      const res = await subscriptionApi.pay({ plan: plan.id, amount, receipt: file, note });
      onDone(res.data);
    } catch (e: any) {
      const d = e.response?.data || {};
      setError(d.error || d.receipt?.[0] || d.amount?.[0] || d.plan?.[0] || "Yuborib bo'lmadi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${plan.name} — ${formatPrice(plan.price)} so'm`}
      footer={
        <button className="btn btn-primary w-full" disabled={busy} onClick={submit}>
          {busy ? 'Yuborilmoqda...' : 'Chekni yuborish'}
        </button>
      }
    >
      <div className="space-y-4">
        {(requisites.card_number || requisites.note) && (
          <div className="rounded-xl bg-gray-50 p-4 space-y-2">
            <div className="text-xs text-gray-500">To'lov uchun karta</div>
            {requisites.card_number && (
              <button onClick={() => copy(requisites.card_number)}
                className="w-full flex items-center justify-between gap-2 text-left">
                <span className="text-lg font-semibold tracking-wider text-gray-900">{requisites.card_number}</span>
                <Copy size={18} className="text-gray-400 shrink-0" />
              </button>
            )}
            {requisites.card_holder && <div className="text-sm text-gray-600">{requisites.card_holder}</div>}
            {requisites.note && <div className="text-sm text-gray-500 whitespace-pre-line">{requisites.note}</div>}
          </div>
        )}

        <div>
          <label className="label">To'langan summa (so'm)</label>
          <input className="input" inputMode="numeric" value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))} />
        </div>

        <div>
          <label className="label">Chek</label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-200 rounded-xl p-4 flex items-center gap-3 text-left active:bg-gray-50">
            <Upload size={20} className="text-gray-400 shrink-0" />
            <span className={`text-sm truncate ${file ? 'text-gray-900' : 'text-gray-500'}`}>
              {file ? file.name : "Chek rasmini yoki PDF ni tanlang"}
            </span>
          </button>
        </div>

        <div>
          <label className="label">Izoh (ixtiyoriy)</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        </div>

        {error && <div className="text-sm text-danger-600">{error}</div>}
      </div>
    </Modal>
  );
};
